import { Heading, Spinner } from '@design-systems/canvas-ui'
import Icon, { WarningFillIcon } from '@design-systems/react-icons'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import { TYPES as DT, useData } from '../context/DataContext'
import { useUi, TYPES as UT } from '../context/UiContext'

import ActionBar from './ActionBar'
import HeaderBar from './HeaderBar'
import ProductCapabilitiesApprovalCards from './ProductCapabilitiesApprovalCards'
import RFIModals from './RFIModals'
import StatusBar from './StatusBar'
import TabBody from './TabBody'
import TabsNav from './TabsNav'

import {
  PARAM_LOCATION_ID,
  PARAM_MANUFACTURING_TYPE_ID,
  PARAM_RETURNURL,
  PARAM_SUPPLIER_ID,
  RFI_TYPE_LOCATION,
  RFI_TYPE_SUPPLIER,
} from '../../../../common/commonConstants'

import { useAnalytics } from '@praxis/analytics/client'
import { useAuth } from '@praxis/component-auth'
import { useEnv } from '@praxis/component-runtime-env'

import useRFIAnalytics from '../hooks/useRFIAnalytics'
import useRFIApprovalActions from '../hooks/useRFIApprovalActions'
import useRFIFetchAll from '../hooks/useRFIFetchAll'
import useRFISaveSubmit from '../hooks/useRFISaveSubmit'

function RFILandingInner({
  rfiType: rfiTypeProp,
  isReadonly: isReadonlyProp,
  userSettings,
}) {
  const { state: d, dispatch: dd } = useData()
  const { state: u, dispatch: ud } = useUi()
  const [sp] = useSearchParams()
  const env = useEnv()
  const auth = useAuth()
  const { sendEvent } = useAnalytics()

  const [auditData, setAuditData] = useState([])
  const [refreshRFIScreen, setRefreshRFIScreen] = useState(false)

  const userId = auth?.session?.userInfo?.lanId
  const supplierId = sp.get(PARAM_SUPPLIER_ID)
  const locationId = sp.get(PARAM_LOCATION_ID)
  const manufacturingTypeId =
    sp.get(PARAM_MANUFACTURING_TYPE_ID) ||
    env?.mfelcm?.appConstants?.defaultManufacturingTypeId
  const encodedRedirectUrl = sp.get(PARAM_RETURNURL)
  const statusColorMapper = env?.mfelcm?.appConstants?.statusColorMapper

  const rfiType = useMemo(
    () =>
      (rfiTypeProp || '').toLowerCase() === RFI_TYPE_LOCATION
        ? RFI_TYPE_LOCATION
        : RFI_TYPE_SUPPLIER,
    [rfiTypeProp],
  )

  const redirectUrl = useMemo(() => {
    try {
      return encodedRedirectUrl ? window.atob(encodedRedirectUrl) : ''
    } catch {
      return ''
    }
  }, [encodedRedirectUrl])

  const cancelAndRedirect = useCallback(() => {
    if (redirectUrl) window.open(redirectUrl, '_self')
  }, [redirectUrl])

  const { pushDataToFirefly, publishRFITimelineData } = useRFIAnalytics({
    isReadonlyProp,
    sendEvent,
    rfiType,
    d,
    auditData,
  })

  const { fetchAll } = useRFIFetchAll({
    isReadonlyProp,
    supplierId,
    userId,
    rfiType,
    manufacturingTypeId,
    locationId,
    dd,
    ud,
  })

  useEffect(() => {
    fetchAll()
  }, [fetchAll, refreshRFIScreen])

  useEffect(() => {
    dd({ type: DT.SET_ERRORCACHE, payload: {} })
  }, [locationId, dd])

  const { saveRfiPayload, submitRfiForm, submitHooks } = useRFISaveSubmit({
    d,
    dd,
    ud,
    manufacturingTypeId,
    rfiType,
    userId,
    auth,
    fetchAll,
    publishRFITimelineData,
    pushDataToFirefly,
    cancelAndRedirect,
  })

  const { approvalHooks, renderInternalApprovalButtons } =
    useRFIApprovalActions({
      d,
      u,
      dd,
      ud,
      rfiType,
      manufacturingTypeId,
      userSettings,
      fetchAll,
      publishRFITimelineData,
      pushDataToFirefly,
      setRefreshRFIScreen,
    })

  const bodyReady =
    !u.loading.fetch && !u.fetchFailed && d.template && d.rfiObject

  const capabilities = useMemo(
    () => d?.rfiObject?.capability_vetting_status ?? [],
    [d?.rfiObject?.capability_vetting_status],
  )

  const isRenderableCapability = useCallback((capability) => {
    const primary = capability?.primary_type
    const secondary = capability?.secondary_type
    const tertiary = capability?.tertiary_type

    if (primary === 'Footwear') {
      return !!secondary && !!tertiary
    }

    if (primary === 'Apparel' && secondary === 'Yarn') {
      return !!tertiary
    }

    if (primary === 'Apparel' && secondary === 'Textile-to-Textile Recycler') {
      return true
    }

    return false
  }, [])

  const hasRenderableCapabilities = useMemo(
    () =>
      Array.isArray(capabilities) && capabilities.some(isRenderableCapability),
    [capabilities, isRenderableCapability],
  )

  const handleManageCapabilities = useCallback(() => {
    ud({ type: UT.SET_ACTION, payload: { userAction: '' } })
    ud({
      type: UT.SET_MODAL,
      payload: {
        productCapabilities: { isVisible: true, isReadOnly: true },
      },
    })
  }, [ud])

  const handleViewCapabilityAuditHistory = useCallback(() => {
    ud({
      type: UT.SET_MODAL,
      payload: {
        capabilityAudit: { isVisible: true, isReadOnly: true },
      },
    })
  }, [ud])

  const handleOnCapabilityAuditDrawerClose = useCallback(() => {
    ud({
      type: UT.SET_MODAL,
      payload: {
        capabilityAudit: { isVisible: false, isReadOnly: true },
      },
    })
  }, [ud])

  return (
    <div id="div-rfi-form" className="hc8-pl-4x hc8-pr-6x hc8-pt-4x">
      {u.loading.fetch && !u.fetchFailed && (
        <div className="height-100 align-centre-centre">
          <Spinner
            key="spinner-rfi-loading"
            data-testid="spinner-rfi1-loading"
            size="lg"
          />
        </div>
      )}
      {bodyReady && (
        <>
          <HeaderBar
            rfiType={rfiType}
            manufacturingTypeId={manufacturingTypeId}
            rfiObject={d.rfiObject}
            template={d.template}
            refreshRFIScreen={refreshRFIScreen}
            isReadonly={u.isReadonly}
            isNonCompliant={u.isNonCompliant || u.isArchived}
            isUFLPA={u.isUFLPA}
            setAuditData={setAuditData}
          />
          <ProductCapabilitiesApprovalCards
            capabilityList={d.capabilityOverview || []}
            template={d.template}
            onManageCapabilities={handleManageCapabilities}
            onViewHistory={handleViewCapabilityAuditHistory}
            onCapabilityAuditDrawerClose={handleOnCapabilityAuditDrawerClose}
            isReadonly={u.isReadonly}
            manufacturingTypeId={manufacturingTypeId}
            isCapabilitiesDisabled={!hasRenderableCapabilities}
            auditData={auditData}
            isCapabilityAuditOpen={u?.modals?.capabilityAudit?.isVisible}
            rfiType={rfiType}
            supplierId={supplierId}
            locationId={locationId}
            rfiObject={d?.rfiObject}
            rfi3VettingStatus={d.rfiObject?.rfi3_vetting_status}
          />
          <TabsNav
            statusContent={
              <StatusBar
                inline
                isReadonly={u.isReadonly}
                rfiType={rfiType}
                rfiObject={d.rfiObject}
                template={d.template}
                masterData={d.masterData}
                statusColorMapper={statusColorMapper}
              />
            }
          />

          <TabBody
            rfiType={rfiType}
            isRenderReadOnlySection={u.isRenderReadOnlySection}
            manufacturingTypeId={manufacturingTypeId}
            saveRfiPayload={saveRfiPayload}
          />
        </>
      )}

      {!u.loading.fetch && !u.fetchFailed && (!d.template || !d.rfiObject) && (
        <div className="height-100 align-centre-centre">
          <Icon svg={WarningFillIcon} />
          <Heading className="hc8-ml-2x" size={3}>
            {(rfiType === RFI_TYPE_LOCATION ? 'RFI2' : 'RFI1') + ' not found'}
          </Heading>
        </div>
      )}

      <ActionBar
        isReadonly={u.isReadonly}
        isNonCompliant={u.isNonCompliant || u.isArchived}
        isULFPA={u.isUFLPA}
        cancelAndRedirect={cancelAndRedirect}
        submitRfiForm={submitRfiForm}
        renderInternalApprovalButtons={renderInternalApprovalButtons}
      />

      <RFIModals
        rfiType={rfiType}
        manufacturingTypeId={manufacturingTypeId}
        submitHooks={submitHooks}
        approvalHooks={approvalHooks}
      />
    </div>
  )
}

export default RFILandingInner
